// Relay status from the Mac (docs/features/relay-passes.md › Usage you can see): over its link, the Mac says how
// this browser's connection goes and what it has used of the relay — wire kind `relayStatus` (0x68), pushed when
// the route changes, otherwise at most every 5 s: {t, route: 'direct' | 'relay', bytesThisConnection,
// passesLeftGB}. Home's route chip reads "Direct" or "Relayed · 120 MB this session · 3.2 GB left on <Mac>";
// its popover adds the detail. The Mac measures (its peer page's getStats); this module only shows what it says.
//
// One hook, in app.js where a Mac's link comes up:
//   watchRelayStatus(mac, link, repaint)   asks once, then keeps mac.relayStatus and repaints on a change that
//                                          shows (a new route at once, numbers at most every 5 s)
import { registerWire } from './wire.js'

registerWire('relayStatus', (link, payload, message) => link.onRelayStatus?.(message))

export const REPAINT_GAP_MS = 5000
export const FREE_NOTE = 'Direct connections are free; relay data is used only when a direct path can’t be made.'

/// The Mac's message, checked: {route, bytes, leftGB} or null.
export function readStatus(message) {
  if (!message || message.t !== 'relayStatus' || (message.route !== 'relay' && message.route !== 'direct')) return null
  const bytes = Number(message.bytesThisConnection), left = Number(message.passesLeftGB)
  return { route: message.route, bytes: Number.isFinite(bytes) && bytes > 0 ? bytes : 0, leftGB: Number.isFinite(left) && left >= 0 ? left : null }
}

/// Decimal, as the relay is billed: "640 KB", "1.2 MB", "120 MB", "1.25 GB".
export function formatBytes(n) {
  const b = Math.max(0, Number(n) || 0)
  if (b < 1e6) return Math.round(b / 1e3) + ' KB'
  if (b < 1e9) return (b < 1e7 ? (b / 1e6).toFixed(1) : Math.round(b / 1e6)) + ' MB'
  return (b / 1e9).toFixed(2) + ' GB'
}

export const formatGB = (gb) => (gb >= 10 ? Math.round(gb) : Math.round(gb * 10) / 10) + ' GB'

/// The chip's words. `status`: readStatus's; `routeInfo`: this browser's own reading (rtcroute.js), used until the
/// Mac has said; `macName`: whose passes.
export function chipText(status, routeInfo, macName) {
  const relayed = status ? status.route === 'relay' : routeInfo?.kind === 'relay'
  if (!relayed) return 'Direct'
  if (!status) return 'Relayed'
  let text = 'Relayed · ' + formatBytes(status.bytes) + ' this session'
  if (status.leftGB != null) text += ' · ' + formatGB(status.leftGB) + ' left on ' + (macName || 'the Mac')
  return text
}

/// The popover's lines, [label, value] — after the route lines it already has — and the note under them.
export function detailLines(status, macName) {
  if (!status) return { lines: [], note: FREE_NOTE }
  const lines = [['Route (the Mac says)', status.route === 'relay' ? 'relayed' : 'direct']]
  if (status.route === 'relay' || status.bytes > 0) lines.push(['Relayed this connection', formatBytes(status.bytes)])
  if (status.leftGB != null) lines.push(['Relay data left on ' + (macName || 'the Mac'), formatGB(status.leftGB)])
  return { lines, note: FREE_NOTE }
}

/// Repaint for this status? A new route at once; otherwise what the chip shows changed, no sooner than 5 s after
/// the last repaint. → {repaint, at} (at: the time to remember as the last repaint).
export function shouldRepaint(prev, next, lastAt, now) {
  if (!next) return { repaint: false, at: lastAt }
  if (!prev || prev.route !== next.route) return { repaint: true, at: now }
  if (now - lastAt < REPAINT_GAP_MS) return { repaint: false, at: lastAt }
  const same = chipText(prev, null, '') === chipText(next, null, '')
  return same ? { repaint: false, at: lastAt } : { repaint: true, at: now }
}

/// The hook: this Mac's link is up. Asks once (an older Mac app never answers — the chip then stays as it was).
export function watchRelayStatus(mac, link, repaint) {
  let lastAt = 0, shown = null
  mac.relayStatus = null
  link.onRelayStatus = (message) => {
    const next = readStatus(message)
    if (!next) return
    mac.relayStatus = next   // the newest, for whatever repaints next
    const r = shouldRepaint(shown, next, lastAt, Date.now())
    lastAt = r.at
    if (r.repaint) { shown = next; repaint() }
  }
  link.send('relayStatus', { t: 'relayStatus' })
}
